import React from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';

const LoadListTable = ({ data, columnVisibility = {} }) => {
  const { mainRows, containerInfo, workOrderInfo } = data;

  const containerNo  = containerInfo?.ContainerNo || '';
  const shippingLine = containerInfo?.Shipping_Line || '';
  const orderNo      = containerInfo?.ORDERNO || workOrderInfo?.Work_OrderNo || '';
  const partyName    = containerInfo?.CONSIGNEE || workOrderInfo?.Client_Name || '';

  const loadListRows = mainRows.map(row => ({
    length:       row.LENGTH,
    width:        row.WIDTH,
    thickness:    row.THICKNESS,
    pcs:          row.PCS,
    quality:      row.QUALITY,
    species:      row.SPECIES,
    glue:         row.GLUE,
    cls:          row.CLASS,
    type:         row.TYPE,
    certificates: row.CERTIFICATES,
    palletNumber: row.LOTNUMBER || 0,
  })).sort((a, b) => a.palletNumber - b.palletNumber);

  const isFSC = (workOrderInfo?.CERTIFICATION_LEGAL || '').replace(/non-fsc/gi, '').toUpperCase().includes('FSC');
  const showCertLine = isFSC && !columnVisibility.certificates;

  const rowCount = loadListRows.length;

  // Count extra columns being shown
  const extraCols = ['palletNo','quality','species','glue','class','type','certificates']
    .filter(k => columnVisibility[k]).length;
  const totalCols = 3 + extraCols;

  // Font scale: larger base fonts, less aggressive scaling
  const getScale = () => {
    const colFactor = totalCols <= 4 ? 1.0 : totalCols <= 6 ? 0.95 : 0.88;
    if (rowCount <= 12) return 1.35 * colFactor;
    if (rowCount <= 18) return 1.2 * colFactor;
    if (rowCount <= 25) return 1.05 * colFactor;
    if (rowCount <= 32) return 0.92 * colFactor;
    return 0.78 * colFactor;
  };

  const scale = getScale();
  const s = (base) => `${(base * scale).toFixed(3)}rem`;

  const getCellPy = (isHeader) => {
    const base = isHeader ? 0.8 : 0.6;
    if (rowCount <= 12) return base * 1.5;
    if (rowCount <= 18) return base * 1.3;
    if (rowCount <= 25) return base * 1.1;
    if (rowCount <= 32) return base * 0.85;
    return base * 0.65;
  };

  const getHeaderGap = () => {
    if (rowCount <= 12) return 2.0;
    if (rowCount <= 18) return 1.6;
    if (rowCount <= 25) return 1.2;
    return 0.9;
  };

  const hdrStyle = {
    border: '1px solid #000',
    fontWeight: 700,
    textAlign: 'center',
    fontSize: s(0.85),
    py: getCellPy(true),
    px: '4px',
    whiteSpace: 'normal',
    wordBreak: 'keep-all',
    lineHeight: 1.3,
  };

  const dataStyle = {
    border: '1px solid #000',
    textAlign: 'center',
    fontSize: s(0.8),
    py: getCellPy(false),
    px: '4px',
    whiteSpace: 'nowrap',
  };

  // PALLET NO. gets ~18% of table, SIZE AND THICKNESS gets ~25%
  // remaining columns share the rest equally
  const palletNoWidth = columnVisibility.palletNo ? '18%' : '0%';
  const sizeWidth = columnVisibility.palletNo ? '22%' : '28%';

  return (
    <Box sx={{
      width: '210mm',
      maxWidth: '210mm',
      p: '5mm 4mm',
      mx: 'auto',
      background: '#fff',
      boxSizing: 'border-box',
      overflow: 'hidden',
      '@media print': {
        width: '210mm',
        maxWidth: '210mm',
        minHeight: 'auto',
        height: 'auto',
        maxHeight: 'none',
        overflow: 'hidden',
        pageBreakAfter: 'auto',
      }
    }}>

      {/* Title */}
      <Box sx={{ textAlign: 'center', mb: getHeaderGap() }}>
        <Typography sx={{
          fontSize: s(1.5),
          fontWeight: 700,
          color: '#000',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
        }}>
          CONTAINER LOADING PLAN
        </Typography>
      </Box>

      {/* Header Info */}
      <Box sx={{ mb: getHeaderGap(), display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Typography sx={{ fontSize: s(1.0), fontWeight: 600, textAlign: 'center' }}>
          CONTAINER NUMBER : {containerNo} ({shippingLine})
        </Typography>
        <Typography sx={{ fontSize: s(1.0), fontWeight: 600, textAlign: 'center' }}>
          PARTY NAME / PO NO. : {partyName} / PO {orderNo}
        </Typography>
        <Typography sx={{ fontSize: s(1.0), fontWeight: 600, textAlign: 'center' }}>
          TOTAL NUMBER OF PALLETS : {loadListRows.length} PALLETS
        </Typography>
        {showCertLine && (
          <Typography sx={{ fontSize: s(1.0), fontWeight: 700, mt: 0.3, textAlign: 'center' }}>
            LEVEL CERTIFICATION: FSC 100%
          </Typography>
        )}
      </Box>

      {/* Table — fixed layout, full width, no overflow */}
      <Box sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #000', width: '100%', overflow: 'hidden' }}>
          <Table sx={{ borderCollapse: 'collapse', width: '100%', tableLayout: 'fixed' }} size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#ffb74d' }}>
                <TableCell sx={{ ...hdrStyle, width: sizeWidth }}>SIZE AND THICKNESS</TableCell>
                {columnVisibility.palletNo     && <TableCell sx={{ ...hdrStyle, width: palletNoWidth }}>PALLET NO.</TableCell>}
                <TableCell sx={hdrStyle}>NO. OF PALLETS</TableCell>
                <TableCell sx={hdrStyle}>PCS. PER PALLET</TableCell>
                {columnVisibility.quality      && <TableCell sx={hdrStyle}>QUALITY</TableCell>}
                {columnVisibility.species      && <TableCell sx={hdrStyle}>SPECIES</TableCell>}
                {columnVisibility.glue         && <TableCell sx={hdrStyle}>GLUE</TableCell>}
                {columnVisibility.class        && <TableCell sx={hdrStyle}>CLASS</TableCell>}
                {columnVisibility.type         && <TableCell sx={hdrStyle}>TYPE</TableCell>}
                {columnVisibility.certificates && <TableCell sx={hdrStyle}>CERTIFICATES</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {loadListRows.map((row, index) => (
                <TableRow key={index} sx={{ backgroundColor: '#fff' }}>
                  <TableCell sx={{ ...dataStyle, width: sizeWidth, whiteSpace: 'normal', textAlign: 'center' }}>
                    {row.length} X {row.width} X {row.thickness} MM
                  </TableCell>
                  {columnVisibility.palletNo     && <TableCell sx={{ ...dataStyle, width: palletNoWidth }}></TableCell>}
                  <TableCell sx={dataStyle}>{row.palletNumber}</TableCell>
                  <TableCell sx={dataStyle}>{row.pcs}</TableCell>
                  {columnVisibility.quality      && <TableCell sx={dataStyle}>{row.quality || ''}</TableCell>}
                  {columnVisibility.species      && <TableCell sx={dataStyle}>{row.species || ''}</TableCell>}
                  {columnVisibility.glue         && <TableCell sx={dataStyle}>{row.glue || ''}</TableCell>}
                  {columnVisibility.class        && <TableCell sx={dataStyle}>{row.cls || ''}</TableCell>}
                  {columnVisibility.type         && <TableCell sx={dataStyle}>{row.type || ''}</TableCell>}
                  {columnVisibility.certificates && <TableCell sx={dataStyle}>{row.certificates || ''}</TableCell>}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};

export default LoadListTable;


